import { useEffect } from "react";

const PAGE_REDIRECT_EVENT = "rio.core.page.redirect";
const FALLBACK_URL = "https://home.rio.cloud";
const RETURN_URL_KEY = "accountSwitchReturnUrl";
const RETURN_URL_TTL_MS = 10 * 60 * 1000;
const AUTH_PARAMS = ["code", "state", "session_state", "iss"];

const getCurrentPageUrl = () => {
  const url = new URL(window.location.href);
  AUTH_PARAMS.forEach(param => url.searchParams.delete(param));
  return url.toString();
};

export const withPostLoginRedirect = (target: string) => {
  try {
    const url = new URL(target);
    if (url.pathname.endsWith("/select-account")) {
      url.searchParams.set("post_login_redirect_uri", getCurrentPageUrl());
    }
    return url.toString();
  } catch {
    return target;
  }
};

const isAllowedTarget = (target: string) => {
  try {
    const { protocol, hostname } = new URL(target);
    return protocol === "https:" && (hostname === "rio.cloud" || hostname.endsWith(".rio.cloud"));
  } catch {
    return false;
  }
};

export const restoreAccountSwitchLocation = () => {
  try {
    const raw = localStorage.getItem(RETURN_URL_KEY);
    if (!raw) return;
    localStorage.removeItem(RETURN_URL_KEY);
    const { url: saved, ts } = JSON.parse(raw) as { url: string; ts: number };
    if (Date.now() - ts > RETURN_URL_TTL_MS) return;
    const url = new URL(saved);
    if (url.origin === window.location.origin && url.href !== window.location.href) {
      window.location.replace(url.toString());
    }
  } catch {
    return;
  }
};

export const useAccountSwitchRedirect = () => {
  useEffect(() => {
    const onPageRedirect = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail;
      const target = detail && isAllowedTarget(detail) ? detail : FALLBACK_URL;
      try {
        localStorage.setItem(RETURN_URL_KEY, JSON.stringify({ url: getCurrentPageUrl(), ts: Date.now() }));
      } catch {
        void 0;
      }
      window.location.href = withPostLoginRedirect(target);
    };

    document.addEventListener(PAGE_REDIRECT_EVENT, onPageRedirect);
    return () => document.removeEventListener(PAGE_REDIRECT_EVENT, onPageRedirect);
  }, []);
};
